import { baseApi } from './baseApi';

export const projectApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // List projects (paginated + filtered)
    listProjects: builder.query({
      query: (params) => ({
        url: '/projects',
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map((p) => ({ type: 'Project', id: p.id || p._id })),
              { type: 'Project', id: 'LIST' },
            ]
          : [{ type: 'Project', id: 'LIST' }],
    }),

    // Get single project
    getProject: builder.query({
      query: (id) => `/projects/${id}`,
      providesTags: (_result, _err, id) => [{ type: 'Project', id }],
    }),

    // Create project
    createProject: builder.mutation({
      query: (body) => ({ url: '/projects', method: 'POST', body }),
      invalidatesTags: [{ type: 'Project', id: 'LIST' }],
    }),

    // Update project
    updateProject: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/projects/${id}`, method: 'PUT', body }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'Project', id },
        { type: 'Project', id: 'LIST' },
        { type: 'Project', id: `dashboard-${id}` },
        { type: 'Project', id: 'STATS' },
        'Budget',
      ],
    }),

    // Delete project (soft)
    deleteProject: builder.mutation({
      query: (id) => ({ url: `/projects/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Project', id: 'LIST' }],
    }),

    // Toggle favorite
    toggleFavorite: builder.mutation({
      query: (id) => ({ url: `/projects/${id}/favorite`, method: 'POST' }),
      // Optimistic update
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate after success to re-sync
          dispatch(projectApi.util.invalidateTags([{ type: 'Project', id: 'LIST' }]));
        } catch {
          // Handled by RTK Query
        }
      },
    }),

    // Project dashboard (aggregate stats)
    getProjectDashboard: builder.query({
      query: (id) => `/projects/${id}/dashboard`,
      providesTags: (_res, _err, id) => [{ type: 'Project', id: `dashboard-${id}` }],
    }),

    // Workspace project stats
    getProjectStats: builder.query({
      query: () => '/projects/stats',
      providesTags: [{ type: 'Project', id: 'STATS' }],
    }),

    // Add member
    addProjectMember: builder.mutation({
      query: ({ projectId, ...body }) => ({
        url: `/projects/${projectId}/members`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_res, _err, { projectId }) => [
        { type: 'Project', id: projectId },
        { type: 'Project', id: `dashboard-${projectId}` },
        { type: 'Project', id: 'LIST' },
      ],
    }),

    // Add members bulk
    addProjectMembersBulk: builder.mutation({
      query: ({ projectId, members }) => ({
        url: `/projects/${projectId}/members/bulk`,
        method: 'POST',
        body: { members },
      }),
      invalidatesTags: (_res, _err, { projectId }) => [
        { type: 'Project', id: projectId },
        { type: 'Project', id: `dashboard-${projectId}` },
        { type: 'Project', id: 'LIST' },
      ],
    }),

    // Remove member
    removeProjectMember: builder.mutation({
      query: ({ projectId, memberId }) => ({
        url: `/projects/${projectId}/members/${memberId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_res, _err, { projectId }) => [
        { type: 'Project', id: projectId },
        { type: 'Project', id: `dashboard-${projectId}` },
        { type: 'Project', id: 'LIST' },
      ],
    }),

    // Update member role
    updateProjectMemberRole: builder.mutation({
      query: ({ projectId, memberId, role }) => ({
        url: `/projects/${projectId}/members/${memberId}`,
        method: 'PATCH',
        body: { role },
      }),
      invalidatesTags: (_res, _err, { projectId }) => [
        { type: 'Project', id: projectId },
        { type: 'Project', id: `dashboard-${projectId}` },
      ],
    }),

    // Update custom statuses
    updateProjectStatuses: builder.mutation({
      query: ({ projectId, customStatuses }) => ({
        url: `/projects/${projectId}/statuses`,
        method: 'PUT',
        body: { customStatuses },
      }),
      invalidatesTags: (_res, _err, { projectId }) => [
        { type: 'Project', id: projectId },
        { type: 'Project', id: `dashboard-${projectId}` },
      ],
    }),

    // ── Clone Project ──
    cloneProject: builder.mutation({
      query: ({ projectId, ...body }) => ({ url: `/projects/${projectId}/clone`, method: 'POST', body }),
      invalidatesTags: [{ type: 'Project', id: 'LIST' }, { type: 'Project', id: 'STATS' }],
    }),

    // ── Project Templates ──
    listProjectTemplates: builder.query({
      query: (params) => ({ url: '/projects/templates', params }),
      providesTags: [{ type: 'ProjectTemplate', id: 'LIST' }],
    }),

    getProjectTemplate: builder.query({
      query: (templateId) => `/projects/templates/${templateId}`,
      providesTags: (_res, _err, id) => [{ type: 'ProjectTemplate', id }],
    }),

    createProjectTemplate: builder.mutation({
      query: (body) => ({ url: '/projects/templates', method: 'POST', body }),
      invalidatesTags: [{ type: 'ProjectTemplate', id: 'LIST' }],
    }),

    createTemplateFromProject: builder.mutation({
      query: ({ projectId, ...body }) => ({ url: `/projects/${projectId}/to-template`, method: 'POST', body }),
      invalidatesTags: [{ type: 'ProjectTemplate', id: 'LIST' }],
    }),

    deleteProjectTemplate: builder.mutation({
      query: (templateId) => ({ url: `/projects/templates/${templateId}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'ProjectTemplate', id: 'LIST' }],
    }),

    createProjectFromTemplate: builder.mutation({
      query: ({ templateId, ...body }) => ({ url: `/projects/templates/${templateId}/use`, method: 'POST', body }),
      invalidatesTags: [{ type: 'Project', id: 'LIST' }, { type: 'Project', id: 'STATS' }],
    }),

    // ── Health Score ──
    getProjectHealth: builder.query({
      query: (projectId) => `/projects/${projectId}/health`,
      providesTags: (_res, _err, id) => [{ type: 'Project', id: `health-${id}` }],
    }),

    // ── Cross-Project Dependencies ──
    addCrossProjectDep: builder.mutation({
      query: ({ projectId, taskId, ...body }) => ({
        url: `/projects/${projectId}/cross-deps/${taskId}`,
        method: 'POST', body,
      }),
      invalidatesTags: (_res, _err, { taskId }) => [{ type: 'Task', id: taskId }],
    }),

    removeCrossProjectDep: builder.mutation({
      query: ({ projectId, taskId, targetTaskId }) => ({
        url: `/projects/${projectId}/cross-deps/${taskId}/${targetTaskId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_res, _err, { taskId }) => [{ type: 'Task', id: taskId }],
    }),

    // ── Notification Preferences (per member) ──
    getProjectNotifPrefs: builder.query({
      query: (projectId) => `/projects/${projectId}/notifications`,
      providesTags: (_res, _err, id) => [{ type: 'Project', id: `notif-${id}` }],
    }),

    updateProjectNotifPrefs: builder.mutation({
      query: ({ projectId, ...prefs }) => ({
        url: `/projects/${projectId}/notifications`,
        method: 'PUT', body: prefs,
      }),
      invalidatesTags: (_res, _err, { projectId }) => [{ type: 'Project', id: `notif-${projectId}` }],
    }),

    // ═══ 4.1 HEALTH & REPORTS ═══
    calculateProjectHealth: builder.mutation({
      query: (projectId) => ({ url: `/projects/${projectId}/health/calculate`, method: 'POST' }),
      invalidatesTags: ['Project'],
    }),
    generateStatusReport: builder.query({
      query: (projectId) => `/projects/${projectId}/status-report`,
    }),

    // ═══ 4.1 RISKS ═══
    listProjectRisks: builder.query({ query: (projectId) => `/projects/${projectId}/risks`, providesTags: ['ProjectRisk'] }),
    createProjectRisk: builder.mutation({ query: ({ projectId, ...body }) => ({ url: `/projects/${projectId}/risks`, method: 'POST', body }), invalidatesTags: ['ProjectRisk'] }),
    updateProjectRisk: builder.mutation({ query: ({ projectId, id, ...body }) => ({ url: `/projects/${projectId}/risks/${id}`, method: 'PUT', body }), invalidatesTags: ['ProjectRisk'] }),
    deleteProjectRisk: builder.mutation({ query: ({ projectId, id }) => ({ url: `/projects/${projectId}/risks/${id}`, method: 'DELETE' }), invalidatesTags: ['ProjectRisk'] }),

    // ═══ 4.1 ISSUES ═══
    listProjectIssues: builder.query({ query: (projectId) => `/projects/${projectId}/issues`, providesTags: ['ProjectIssue'] }),
    createProjectIssue: builder.mutation({ query: ({ projectId, ...body }) => ({ url: `/projects/${projectId}/issues`, method: 'POST', body }), invalidatesTags: ['ProjectIssue'] }),
    updateProjectIssue: builder.mutation({ query: ({ projectId, id, ...body }) => ({ url: `/projects/${projectId}/issues/${id}`, method: 'PUT', body }), invalidatesTags: ['ProjectIssue'] }),
    deleteProjectIssue: builder.mutation({ query: ({ projectId, id }) => ({ url: `/projects/${projectId}/issues/${id}`, method: 'DELETE' }), invalidatesTags: ['ProjectIssue'] }),

    // ═══ 4.1 DECISIONS ═══
    listProjectDecisions: builder.query({ query: (projectId) => `/projects/${projectId}/decisions`, providesTags: ['ProjectDecision'] }),
    createProjectDecision: builder.mutation({ query: ({ projectId, ...body }) => ({ url: `/projects/${projectId}/decisions`, method: 'POST', body }), invalidatesTags: ['ProjectDecision'] }),
    updateProjectDecision: builder.mutation({ query: ({ projectId, id, ...body }) => ({ url: `/projects/${projectId}/decisions/${id}`, method: 'PUT', body }), invalidatesTags: ['ProjectDecision'] }),

    // ═══ 4.1 CHANGE REQUESTS ═══
    listChangeRequests: builder.query({ query: (projectId) => `/projects/${projectId}/change-requests`, providesTags: ['ProjectCR'] }),
    createChangeRequest: builder.mutation({ query: ({ projectId, ...body }) => ({ url: `/projects/${projectId}/change-requests`, method: 'POST', body }), invalidatesTags: ['ProjectCR'] }),
    approveChangeRequest: builder.mutation({ query: ({ projectId, id, ...body }) => ({ url: `/projects/${projectId}/change-requests/${id}/approve`, method: 'PUT', body }), invalidatesTags: ['ProjectCR'] }),

    // ═══ 4.1 LESSONS ═══
    listProjectLessons: builder.query({ query: (projectId) => `/projects/${projectId}/lessons`, providesTags: ['ProjectLesson'] }),
    createProjectLesson: builder.mutation({ query: ({ projectId, ...body }) => ({ url: `/projects/${projectId}/lessons`, method: 'POST', body }), invalidatesTags: ['ProjectLesson'] }),

    // ═══ 4.1 RACI ═══
    getProjectRaci: builder.query({ query: (projectId) => `/projects/${projectId}/raci`, providesTags: ['ProjectRaci'] }),
    updateProjectRaci: builder.mutation({ query: ({ projectId, entries }) => ({ url: `/projects/${projectId}/raci`, method: 'PUT', body: { entries } }), invalidatesTags: ['ProjectRaci'] }),

    // ═══ 4.2 CLONE ═══
    cloneProjectEnhanced: builder.mutation({ query: ({ projectId, ...body }) => ({ url: `/projects/${projectId}/clone-enhanced`, method: 'POST', body }), invalidatesTags: ['Project'] }),

    // ═══ 4.3 PHASES ═══
    listProjectPhases: builder.query({ query: (projectId) => `/projects/${projectId}/phases`, providesTags: ['ProjectPhase'] }),
    createProjectPhase: builder.mutation({ query: ({ projectId, ...body }) => ({ url: `/projects/${projectId}/phases`, method: 'POST', body }), invalidatesTags: ['ProjectPhase'] }),
    updateProjectPhase: builder.mutation({ query: ({ projectId, phaseId, ...body }) => ({ url: `/projects/${projectId}/phases/${phaseId}`, method: 'PUT', body }), invalidatesTags: ['ProjectPhase'] }),
    deleteProjectPhase: builder.mutation({ query: ({ projectId, phaseId }) => ({ url: `/projects/${projectId}/phases/${phaseId}`, method: 'DELETE' }), invalidatesTags: ['ProjectPhase'] }),
    approvePhaseGate: builder.mutation({ query: ({ projectId, phaseId }) => ({ url: `/projects/${projectId}/phases/${phaseId}/approve-gate`, method: 'POST' }), invalidatesTags: ['ProjectPhase'] }),
    updateGateCheck: builder.mutation({ query: ({ projectId, phaseId, checkId, completed }) => ({ url: `/projects/${projectId}/phases/${phaseId}/gate-check/${checkId}`, method: 'PUT', body: { completed } }), invalidatesTags: ['ProjectPhase'] }),
    initDefaultPhases: builder.mutation({ query: ({ projectId, phaseNames }) => ({ url: `/projects/${projectId}/phases/init`, method: 'POST', body: { phaseNames } }), invalidatesTags: ['ProjectPhase'] }),

    // ═══ 4.4 SNAPSHOTS ═══
    listProjectSnapshots: builder.query({ query: (projectId) => `/projects/${projectId}/snapshots`, providesTags: ['ProjectSnapshot'] }),
    createProjectSnapshot: builder.mutation({ query: ({ projectId, ...body }) => ({ url: `/projects/${projectId}/snapshots`, method: 'POST', body }), invalidatesTags: ['ProjectSnapshot'] }),
    archiveProject: builder.mutation({ query: (projectId) => ({ url: `/projects/${projectId}/archive`, method: 'POST' }), invalidatesTags: ['Project'] }),
    restoreProject: builder.mutation({ query: ({ projectId, dateOffsetDays }) => ({ url: `/projects/${projectId}/restore`, method: 'POST', body: { dateOffsetDays } }), invalidatesTags: ['Project'] }),
    searchArchivedProjects: builder.query({ query: (params) => ({ url: '/projects/archived/search', params }), providesTags: ['Project'] }),

    // ═══ R5 (2026-04-20) — Per-project permission overrides ═══
    listProjectPermissionOverrides: builder.query({
      query: (projectId) => `/projects/${projectId}/permission-overrides`,
      providesTags: ['Project'],
    }),
    setProjectPermissionOverrides: builder.mutation({
      query: ({ projectId, userId, grants, denies }) => ({
        url: `/projects/${projectId}/permission-overrides/${userId}`,
        method: 'PUT',
        body: { grants, denies },
      }),
      invalidatesTags: ['Project'],
    }),
    clearProjectPermissionOverrides: builder.mutation({
      query: ({ projectId, userId }) => ({
        url: `/projects/${projectId}/permission-overrides/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Project'],
    }),
  }),
});

export const {
  useListProjectsQuery,
  useGetProjectQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useToggleFavoriteMutation,
  useGetProjectDashboardQuery,
  useGetProjectStatsQuery,
  useAddProjectMemberMutation,
  useAddProjectMembersBulkMutation,
  useRemoveProjectMemberMutation,
  useUpdateProjectMemberRoleMutation,
  useUpdateProjectStatusesMutation,
  // R5 — per-project permission overrides
  useListProjectPermissionOverridesQuery,
  useSetProjectPermissionOverridesMutation,
  useClearProjectPermissionOverridesMutation,
  // Clone
  useCloneProjectMutation,
  // Templates
  useListProjectTemplatesQuery,
  useGetProjectTemplateQuery,
  useCreateProjectTemplateMutation,
  useCreateTemplateFromProjectMutation,
  useDeleteProjectTemplateMutation,
  useCreateProjectFromTemplateMutation,
  // Health
  useGetProjectHealthQuery,
  // Cross-project deps
  useAddCrossProjectDepMutation,
  useRemoveCrossProjectDepMutation,
  // Notification prefs
  useGetProjectNotifPrefsQuery,
  useUpdateProjectNotifPrefsMutation,
  // 4.1 Health & Reports
  useCalculateProjectHealthMutation, useGenerateStatusReportQuery,
  // 4.1 Risks
  useListProjectRisksQuery, useCreateProjectRiskMutation, useUpdateProjectRiskMutation, useDeleteProjectRiskMutation,
  // 4.1 Issues
  useListProjectIssuesQuery, useCreateProjectIssueMutation, useUpdateProjectIssueMutation, useDeleteProjectIssueMutation,
  // 4.1 Decisions
  useListProjectDecisionsQuery, useCreateProjectDecisionMutation, useUpdateProjectDecisionMutation,
  // 4.1 Change Requests
  useListChangeRequestsQuery, useCreateChangeRequestMutation, useApproveChangeRequestMutation,
  // 4.1 Lessons
  useListProjectLessonsQuery, useCreateProjectLessonMutation,
  // 4.1 RACI
  useGetProjectRaciQuery, useUpdateProjectRaciMutation,
  // 4.2 Clone
  useCloneProjectEnhancedMutation,
  // 4.3 Phases
  useListProjectPhasesQuery, useCreateProjectPhaseMutation, useUpdateProjectPhaseMutation, useDeleteProjectPhaseMutation,
  useApprovePhaseGateMutation, useUpdateGateCheckMutation, useInitDefaultPhasesMutation,
  // 4.4 Snapshots & Archive
  useListProjectSnapshotsQuery, useCreateProjectSnapshotMutation,
  useArchiveProjectMutation, useRestoreProjectMutation, useSearchArchivedProjectsQuery,
} = projectApi;
